
import { PaymentDetails, Payment } from "@/types/payment";
import { supabase } from "@/lib/supabase";

export class PaymentService {
  private static async initializeTransaction(details: PaymentDetails): Promise<{ url: string; reference: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User must be authenticated to make payments");
      }

      // Use the correct Supabase edge function URL
      const response = await supabase.functions.invoke('payment-initialize', {
        body: details,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      if (response.error) {
        console.error("Payment initialization error:", response.error);
        throw new Error(response.error.message || "Failed to initialize payment");
      }

      return response.data;
    } catch (error) {
      console.error("Payment initialization error:", error);
      throw error;
    }
  }

  static async createPayment(details: PaymentDetails): Promise<{ url: string; reference: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error("User must be authenticated to make payments");
    }

    // Initialize the transaction with Flutterwave via the edge function.
    // The edge function handles creating the payment record in the database.
    return await this.initializeTransaction(details);
  }

  static async verifyPayment(reference: string): Promise<{ payment: Payment; status: string; message: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User must be authenticated to verify payments");
      }
      
      const response = await supabase.functions.invoke('payment-verify', {
        body: { reference },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      if (response.error) {
        console.error("Payment verification error:", response.error);
        throw new Error(response.error.message || "Failed to verify payment");
      }

      return response.data;
    } catch (error) {
      console.error("Payment verification error:", error);
      throw error;
    }
  }
}
